import { Store } from 'vuex';
import { AxiosResponse, AxiosInstance, AxiosError } from 'axios';
import { RewardPoints, CustomerProfile, LoyaltyProfile, CustomerSubscriptionStatus, MarketingChannel, MarketingContext } from '@/models';
import { ApiService } from '../api/api.service';
import { MUTATIONS as CUSTOMER_MUTATIONS } from '@/store/customer';
import { MUTATIONS as REWARD_MUTATIONS } from '@/store/reward';
import { AppState } from '@/store';
import { Logger } from '../logger';
import { CustomerService } from '../customer/customer.service';
import { AnalyticsEventAction, AnalyticsEventCategory, AppGoogleAnalyticsService } from '../google/google-analytics.services';
import { PromiseCacher } from '@/helpers/async';
import { makeSafariSafeDate, isUserLoyaltyEligible } from '@/helpers';
import { MUTATIONS } from '@/store/ui';
import { FeatureFlagsService } from '../feature-flags/feature-flags.service';
import { ConfigService } from '../config/config.service';
import moment, { MomentInput } from 'moment';
import { OrderService } from '../order/order.service';
import { AnalyticsService } from '../analytics/analytics.service';

export interface EnrollmentOptions {
  customer: CustomerProfile | null;
  pageTitle?: string;
  shouldSubscribeToMarketingEmail?: boolean;
  shouldSubscribeToMarketingSMS?: boolean;
}

export interface LoyaltyService {
  getLoyaltyProfile(customer: CustomerProfile): LoyaltyProfile | null;
  isLoyaltyEligible(customer: CustomerProfile | null): boolean;
  isEnrolledInLoyalty(customer: CustomerProfile | null): boolean;
  setLoyaltyForced(forced: boolean): void;
  setLoyaltyIsEnrolled(isEnrolled: boolean): void;
  refreshPoints(ignoreThrottle?: boolean): Promise<RewardPoints>;
  enrollInLoyalty(options: EnrollmentOptions): Promise<void>;
  unenrollInLoyalty(reasonForLeaving?: string): Promise<void>;
  declineLoyalty(): void;
  getLoyaltyHistory(pageCount: number, lastDate?: Date, lastId?: string): Promise<boolean>;
  clearLoyaltyHistory(): void;
  postLoyaltyIdToParentFrame(): void;
  getVibesSmartLink(loyaltyId: string): Promise<string>;
  isEverEnrolledInLoyalty(customer: CustomerProfile | null): boolean | null;
}

const LOYALTY_SUBSCRIPTION_CONTEXT = 'loyalty';
export class AppLoyaltyService extends ApiService implements LoyaltyService {
  lastRefreshedPointsTime: MomentInput | undefined;

  constructor(
    private customer: CustomerService,
    store: Store<AppState>,
    httpClient: AxiosInstance,
    private orderService: OrderService,
    logger: Logger,
    private promiseCacher: PromiseCacher,
    private googleAnalytics: AppGoogleAnalyticsService,
    private featureFlags: FeatureFlagsService,
    private config: ConfigService,
    protected analytics: AnalyticsService,
  ) {
    super(store, httpClient, logger, analytics);
  }

  isLoyaltyOnline(): boolean {
    const flags = this.store.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isRewardsOnline) {
      return false;
    }
 
    return true;
  }

  getLoyaltyProfile(customer: CustomerProfile): LoyaltyProfile | null {
    if (!customer.subscriptions) {
      return null;
    }

    const loyaltySubscription = customer.subscriptions.find(
      s => s.context == LOYALTY_SUBSCRIPTION_CONTEXT && s.status != CustomerSubscriptionStatus.Unsubscribed && s.status != CustomerSubscriptionStatus.UnsubscribePending,
    );

    const memberId = loyaltySubscription && loyaltySubscription.subscriptionId;

    const startDate = loyaltySubscription && loyaltySubscription.subscribedAt;

    if (!memberId) {
      return null;
    }

    return {
      firstName: customer.firstName,
      lastName: customer.lastName,
      memberId,
      subscribeDate: startDate,
    };
  }

  isLoyaltyEligible(customer: CustomerProfile | null): boolean {
    if (!customer || customer.isGuest) {
      return false;
    }
    return isUserLoyaltyEligible(this.store.state.ui.countryProfile.countryCode, customer.country || 'US');
  }

  isEnrolledInLoyalty(customer: CustomerProfile | null): boolean {
    if (!customer || customer.isGuest) {
      return false;
    }

    return this.store.state.customer.loyalty.forced || !!this.getLoyaltyProfile(customer);
  }

  isEverEnrolledInLoyalty(customer: CustomerProfile | null): boolean | null {
    if (!customer || customer.isGuest) {
      return null;
    }

    const loyaltySubscription = customer.subscriptions.find(s => s.context == LOYALTY_SUBSCRIPTION_CONTEXT);
    return !!loyaltySubscription;
  }

  async refreshPoints(ignoreThrottle: boolean = false): Promise<any> {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isLoyaltyOnline()) {
      return Promise.resolve();
    }

    const now = moment();
    const elapsedTimeInMinutes = moment.duration(now.diff(this.lastRefreshedPointsTime)).asMinutes();

    if (!ignoreThrottle && this.lastRefreshedPointsTime !== undefined && elapsedTimeInMinutes < this.config.getRefreshPointsThrottleTimeInMinutes()) {
      return;
    }

    try {
      return this.promiseCacher.exec('refreshPoints', false, async () => {
        const points = await this.httpClient
          .get(`/loyalty/v2/points`)
          .then((r: AxiosResponse<RewardPoints>) => {
            this.lastRefreshedPointsTime = moment();
            this.store.commit(REWARD_MUTATIONS.SET_UNREACHABLE, false);
            return r.data;
          })
          .catch(e => this.handleError('refreshPoints', e));
        this.store.commit(CUSTOMER_MUTATIONS.SET_REWARD_POINTS, points);
        window.dispatchEvent(new Event('cmg-vuex-rewards-updated'));
        return points;
      }, true);
    } catch (e: any) {
      return this.handleError('refreshPoints', e);
    }
  }

  async enrollInLoyalty({ customer, pageTitle, shouldSubscribeToMarketingEmail = true, shouldSubscribeToMarketingSMS = false }: EnrollmentOptions) {
    this.store.commit(MUTATIONS.SET_ENROLLING_IN_REWARDS, true);

    const promises = [];
    if (shouldSubscribeToMarketingEmail && shouldSubscribeToMarketingSMS) {
      promises.push(this.customer.subscribeToMarketingChannel(MarketingChannel.EmailAndSms, MarketingContext.LoyaltyAndMarketing));
    } else if (shouldSubscribeToMarketingEmail && !shouldSubscribeToMarketingSMS) {
      promises.push(this.customer.subscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.LoyaltyAndMarketing));
    } else if (shouldSubscribeToMarketingSMS && !shouldSubscribeToMarketingEmail) {
      promises.push(this.customer.subscribeToMarketingChannel(MarketingChannel.Sms, MarketingContext.LoyaltyAndMarketing));
    } else {
      promises.push(this.customer.subscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Loyalty));
    }

    const results = await Promise.allSettled(promises);

    results.forEach(result => {
      if (result.status === 'rejected') {
        this.logger.error('Unable to subscribe to channel', undefined, result.reason);
      }
    });

    if (results[0].status === 'fulfilled') {
      // We don't care about sms subscription failures for this analytics event.
      this.googleAnalytics.sendLoyaltyEnrollmentEvent(customer, pageTitle);
    }

    this.store.commit(MUTATIONS.SET_ENROLLING_IN_REWARDS, false);

    this.setLoyaltyForced(true);
    this.setLoyaltyIsEnrolled(true);

    // This is to let popExplainerModal() function in cmg-app.ts know that this customer should be treated as a new account
    // so it will not show the Rewards 2.0 Onboarding Modal meant for existing loyalty user if they rejoin in the same session
    this.customer.setNewAccountStatus(true);

    await this.updateCustomerLoyaltyEnrollmentStatus();

    this.analytics.addRewardsEnrollEventToStore({'value':1});
  }

  async unenrollInLoyalty(reasonForLeaving: string = '') {
    this.declineLoyalty();
    await this.customer.unsubscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Loyalty);
    await this.updateCustomerLoyaltyEnrollmentStatus();
    
    this.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.Rewards,
      action: AnalyticsEventAction.UnenrollSuccess,
      label: reasonForLeaving,
    });

    this.setLoyaltyForced(false);
    this.setLoyaltyIsEnrolled(false);
  }

  async updateCustomerLoyaltyEnrollmentStatus() {
    if (this.store.state.customer.jwt) {
      const order = this.orderService.getCurrentOrder();
      if (order) {
        try {
          setTimeout(async () => {
            await this.orderService.updateCustomerLoyaltyEnrollmentStatus(order, this.store.state.customer.jwt);
          }, 3000);
        } catch {
          this.orderService.resetPendingOrders();
          this.logger.warn(`session likely expired so customer lost their order: ${order.order.orderId}`);
        }
      }
    }
  }

  // Similar to isEnrolled - however it stays null when logging in, until a user enrolls/unenrolls.
  setLoyaltyForced(forced: boolean) {
    this.store.commit(CUSTOMER_MUTATIONS.SET_LOYALTY_FORCED, forced);
  }

  // isEnrolled = true when the user is logged in and enrolled in Rewards,
  // ie their Loyalty subscription status is either "Subscribed" or "SubscribePending"
  setLoyaltyIsEnrolled(isEnrolled: boolean) {
    this.store.commit(CUSTOMER_MUTATIONS.SET_LOYALTY_IS_ENROLLED, isEnrolled);
  }

  declineLoyalty() {
    this.store.commit(CUSTOMER_MUTATIONS.SET_LOYALTY_DECLINED, true);
  }

  async getLoyaltyHistory(pageCount: number, lastDate?: Date, lastId?: string): Promise<boolean> {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isLoyaltyOnline()) {
      return Promise.resolve(false);
    }

    let daysTillPointsExpire = 0;
    let daysPointsExpired = 0;

    const history = await this.httpClient
      .get(`/loyalty/v2/activities?pageCount=${pageCount}${lastDate ? '&lastDate=' + ( ( lastDate.getTime() / 1000 ) - 1 ): ''}${lastId ? '&lastId=' + lastId : ''}`)
      .then((r: AxiosResponse) => {
        this.store.commit(REWARD_MUTATIONS.SET_UNREACHABLE, false);
        daysTillPointsExpire = r.data.daysTillPointsExpire;
        daysPointsExpired = r.data.daysPointsExpired;

        return r.data.activities.map((activity: any) => {
          activity.activityDate = makeSafariSafeDate(activity.activityDate);
          return activity;
        });
      })
      .catch(e => this.handleError('getLoyaltyHistory', e));

    if (!lastDate) {
      this.store.commit(CUSTOMER_MUTATIONS.SET_DAYS_TILL_POINTS_EXPIRE, daysTillPointsExpire);
      this.store.commit(CUSTOMER_MUTATIONS.SET_DAYS_POINTS_EXPIRED, daysPointsExpired);
    }

    this.store.commit(CUSTOMER_MUTATIONS.SET_LOYALTY_POINTS_HISTORY, this.store.state.customer.loyalty.pointsHistory.concat(history));

    //was this page full or are we at the end?
    return history.length >= pageCount;
  }

  clearLoyaltyHistory(): void {
    this.store.commit(CUSTOMER_MUTATIONS.SET_LOYALTY_POINTS_HISTORY, []);
  }

  private handleError(operationName: string, e: AxiosError) {
    if (e.response) {
      this.store.commit(REWARD_MUTATIONS.SET_UNREACHABLE, e.response.status >= 400);
    }

    return this.logAndThrowApiError(operationName, e);
  }

  /**
   * This method is used to enable the current vendor sign-in mechanism (since Customer Auth cannot support OAuth) -
   * A vendor can embed our vendor sign in / create account pages in an iframe and add a message listener on their
   * window object for the the loyalty id which we post to them here
   */
  postLoyaltyIdToParentFrame(): void {
    const { profile } = this.store.state.customer;
    const loyaltyId = profile && this.getLoyaltyProfile(profile)?.memberId;
    /**
     * No need to specify targetOrigin here, '*' is safe since CSP frame-ancestors directive will protect against
     * unknown sites embedding NOO in iframe
     */
    loyaltyId && window.parent.postMessage(loyaltyId, '*');
  }

  async getVibesSmartLink(loyaltyId: string): Promise<string> {
    return this.httpClient
      .get(`/loyalty/v2/customers/smartlink?loyaltyId=${loyaltyId}`)
      .then((r: AxiosResponse<{ smartLink: string }>) => {
        return r.data.smartLink;
        })
      .catch(e => this.handleError('getVibesSmartLink', e));
  }
}
